<template>
	<v-expansion-panels v-model="expanded">
		<v-expansion-panel v-if="interaction && canView">
			<v-expansion-panel-header :color="headerColor" hide-actions>
				<v-btn
					class="v-expansion-panel-header__icon"
					:class="{
						'v-expansion-panel-header__icon--active':
							expanded === 0,
					}"
					icon
				>
					<v-icon>mdi-chevron-down</v-icon>
				</v-btn>
				<v-row no-gutters>
					<v-col class="ma-auto">
						Interaction {{ currentInteraction.Index + 1 }}
						<span v-if="currentInteraction.Description">
							- {{ currentInteraction.Description }}
						</span>
					</v-col>
					<v-col>
						<v-btn
							v-if="canDelete"
							fab
							small
							icon
							elevation="2"
							class="action-btn float-right"
							@click.stop="remove()"
						>
							<v-icon color="error" small>mdi-delete</v-icon>
						</v-btn>
						<v-btn
							v-if="!editMode && showEditButton"
							fab
							small
							icon
							elevation="2"
							class="action-btn float-right"
							@click.stop="
								editMode = true;
								expanded = 0;
							"
						>
							<v-icon color="primary" small> mdi-pencil</v-icon>
						</v-btn>
						<v-btn
							v-if="editMode"
							fab
							small
							icon
							elevation="2"
							class="action-btn float-right"
							@click.stop="submit(index)"
						>
							<v-icon color="primary" small>
								mdi-content-save
							</v-icon>
						</v-btn>
					</v-col>
				</v-row>
			</v-expansion-panel-header>
			<v-expansion-panel-content>
				<template v-if="!editMode">
					<v-row>
						<v-col>
							<div>
								<h4>Conditions</h4>
								<v-data-table
									v-if="
										interaction.Conditions &&
										interaction.Conditions.length > 0
									"
									dense
									fixed-header
									elevation="0"
									:headers="conditionHeaders"
									:items="resolvedConditions"
									:hide-default-footer="true"
								/>
								<div v-else class="text-center pa-4">
									No conditions available.
								</div>
							</div>
						</v-col>
						<v-col>
							<div><h4>Actions</h4></div>
							<v-data-table
								v-if="
									interaction.Actions &&
									interaction.Actions.length > 0
								"
								dense
								fixed-header
								elevation="0"
								:headers="actionHeaders"
								:items="resolvedActions"
								:hide-default-footer="true"
							/>
							<div v-else class="text-center pa-4">
								No actions available.
							</div>
						</v-col>
					</v-row>
				</template>
				<v-form v-else ref="form" :disabled="!disableForm">
					<v-row>
						<v-col cols="12">
							<v-text-field
								v-model="interaction.Description"
								label="Interaction Description"
							></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<div>
								<h4>Conditions</h4>
								<form-condition
									:ref="'formCondition_' + index"
									v-for="(
										condition, index
									) in interaction.Conditions"
									:condition="interaction.Conditions[index]"
									:index="index"
									:key="index"
									:newInteraction="currentInteraction.IsNew"
									@remove="removeCondition(index)"
								/>
								<v-btn
									v-if="
										(canEdit &&
											currentInteraction.IsNew ==
												false) ||
										(canAdd &&
											currentInteraction.IsNew == true)
									"
									small
									class="primary"
									width="100%"
									@click="addCondition()"
								>
									Add new condition
								</v-btn>
							</div>
						</v-col>
						<v-col>
							<div><h4>Actions</h4></div>
							<form-action
								:ref="'formAction_' + index"
								v-for="(action, index) in interaction.Actions"
								:key="index"
								:index="index"
								:action="interaction.Actions[index]"
								:newInteraction="currentInteraction.IsNew"
								@remove="removeAction(index)"
							/>
							<v-btn
								v-if="
									(canEdit &&
										currentInteraction.IsNew == false) ||
									(canAdd && currentInteraction.IsNew == true)
								"
								small
								class="primary"
								width="100%"
								@click="addAction()"
							>
								Add new action
							</v-btn>
						</v-col>
					</v-row>
				</v-form>
			</v-expansion-panel-content>
		</v-expansion-panel>
	</v-expansion-panels>
</template>
<script>
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import FormCondition from './FormCondition.vue';
import FormAction from './FormAction.vue';

import { InteractionsActions } from '../../../../store/actions';
import { InteractionsGetters } from '../../../../store/getters';
import { Permissions } from '../../../../enums/permissionsEnum';
import { InventoryFormOperatorTypes } from '../../../../enums/inventoryFormOperator';
import { InventoryFormActionTypes } from '../../../../enums/inventoryFormAction';
import permissionsMixin from '../../../../mixins/permissionsMixin';
import { mapGetters } from 'vuex';
export default {
	name: 'FormInteraction',
	components: { FormCondition, FormAction },
	mixins: [permissionsMixin],
	props: {
		index: Number,
		currentInteraction: Object,
	},
	computed: {
		...mapGetters({
			formFields: [InteractionsGetters.FORM_FIELDS],
		}),
		disableForm() {
			return (
				(this.canEdit && this.currentInteraction.IsNew == false) ||
				(this.canAdd && this.currentInteraction.IsNew == true)
			);
		},
		canAdd() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Interactions.Add,
			]);
		},
		canEdit() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Interactions.Edit,
			]);
		},
		canView() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Interactions.View,
			]);
		},
		canDelete() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Interactions.Delete,
			]);
		},
		showEditButton() {
			if (this.canAdd && this.interaction.IsNew === true) return true;
			if (this.canEdit && this.interaction.IsNew === false) return true;
			return false;
		},
		headerColor() {
			if (this.hasChanges) return '#ffc00030';
			return '#ffffff';
		},
		resolvedConditions() {
			return this.interaction.Conditions.map((c) => ({
				Field: this.getField(c.FormFieldId),
				Operator: this.getOperator(c.Operator),
				Value: c.Value,
			}));
		},
		resolvedActions() {
			return this.interaction.Actions.map((a) => ({
				Field: this.getField(a.FormFieldId),
				Action: this.getAction(a.Value),
				Comment: a.Comment,
			}));
		},
	},
	watch: {
		interaction: {
			handler: function () {
				if (!this.interaction) this.hasChanges = false;
				if (!this.currentInteraction) this.hasChanges = false;
				let changes = this.getChanges(
					[this.currentInteraction],
					[this.interaction]
				);
				this.hasChanges = changes.length > 0;
			},
			deep: true,
		},
		expanded: {
			handler: function (newVal) {
				if (newVal === -1 && this.editMode) {
					this.cancelEdit();
				}
			},
		},
	},
	data() {
		return {
			interaction: null,
			hasChanges: true,
			editMode: false,
			expanded: 0,
			conditionHeaders: [
				{ text: 'Field', value: 'Field' },
				{ text: 'Operator', value: 'Operator' },
				{ text: 'Value', value: 'Value' },
			],
			actionHeaders: [
				{ text: 'Field', value: 'Field' },
				{ text: 'Action', value: 'Action' },
				{ text: 'Comment', value: 'Comment' },
			],
			operators: InventoryFormOperatorTypes,
			actions: InventoryFormActionTypes,
		};
	},
	mounted() {
		if (this.currentInteraction) {
			this.interaction = JSON.parse(
				JSON.stringify(this.currentInteraction)
			);
		}
		this.expanded = -1;
	},

	methods: {
		getChanges(oldArray, newArray) {
			var changes, i, item, j, len;
			if (JSON.stringify(oldArray) === JSON.stringify(newArray)) {
				return false;
			}
			changes = [];
			for (i = j = 0, len = newArray.length; j < len; i = ++j) {
				item = newArray[i];
				if (JSON.stringify(item) !== JSON.stringify(oldArray[i])) {
					changes.push(item);
				}
			}
			return changes;
		},
		addCondition() {
			this.interaction.Conditions.push(
				Object.assign(
					{},
					{ DataFieldId: null, Operator: 0, Value: null }
				)
			);
		},
		removeCondition(index) {
			this.interaction.Conditions.splice(index, 1);
		},
		addAction() {
			this.interaction.Actions.push(
				Object.assign(
					{},
					{ DataFieldId: null, Value: 1, Comment: null }
				)
			);
		},
		removeAction(index) {
			this.interaction.Actions.splice(index, 1);
		},

		validate() {
			let valid = true;
			this.interaction.Conditions.forEach((e) => {
				var index = this.interaction.Conditions.indexOf(e);
				if (this.$refs['formCondition_' + index]) {
					let conditionValid =
						this.$refs['formCondition_' + index][0].validate();
					valid = valid && conditionValid;
				}
			});
			this.interaction.Actions.forEach((e) => {
				var index = this.interaction.Actions.indexOf(e);
				if (this.$refs['formAction_' + index]) {
					let conditionValid =
						this.$refs['formAction_' + index][0].validate();
					valid = valid && conditionValid;
				}
			});
			return valid;
		},

		submit() {
			if (!this.validate()) return;

			let interactionToSubmit = Object.assign(
				{},
				{
					Actions: this.interaction.Actions,
					Conditions: this.interaction.Conditions,
					Interaction: {
						InventoryId: this.interaction.InventoryId,
						Order: this.interaction.Order,
						Id: this.interaction.Id,
						Description: this.interaction.Description,
					},
					OnBlur: this.interaction.OnBlur,
					OnChange: this.interaction.OnChange,
					OnFocus: this.interaction.OnFocus,
					OnKeyPress: this.interaction.OnKeyPress,
					OnLoad: this.interaction.OnLoad,
					RevertOnOppositeTrigger:
						this.interaction.RevertOnOppositeTrigger,
					RevertOnOppositeValue:
						this.interaction.RevertOnOppositeValue,
				}
			);

			let action = InteractionsActions.ADD;
			if (this.interaction.Id) {
				action = InteractionsActions.UPDATE;
			}

			this.$store
				.dispatch(action, interactionToSubmit)
				.then(({ success, data }) => {
					if (success === false) return;
					if (data) {
						this.interaction = data;
					}
					this.interaction.IsNew = false;
					this.hasChanges = false;
					this.interaction.Index = this.currentInteraction.Index;
					this.editMode = false;
					this.$emit('saved', {
						oldItem: this.currentInteraction,
						newItem: JSON.parse(JSON.stringify(this.interaction)),
					});
				});
		},

		remove() {
			ConfirmDialog({
				message: 'Are you sure you want to delete this interaction?',
			}).then((isDelete) => {
				if (isDelete) {
					this.$emit('remove', this.currentInteraction);
				}
			});
		},
		cancelEdit() {
			this.interaction = JSON.parse(
				JSON.stringify(this.currentInteraction)
			);
			this.editMode = false;
			this.expanded = 0;
		},
		getField(id) {
			let field = this.formFields.find((f) => f.Id == id);
			return field ? field.Label : id;
		},
		getOperator(value) {
			let op = this.operators.find((o) => o.value == value);
			return op ? op.text : value;
		},
		getAction(value) {
			let act = this.actions.find((a) => a.value == value);
			return act ? act.text : value;
		},
	},
};
</script>
<style scoped>
.indexCol {
	max-width: 35px;
}

.actionCol {
	max-width: 100px;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: flex-end;
}
</style>
